import { NgClass } from '@angular/common';
import { AfterViewInit, Component, OnInit, signal, WritableSignal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { IBAN } from 'ibankit';
import { ConfirmationService, LazyLoadEvent, MenuItem, MessageService, PrimeTemplate, SelectItem } from 'primeng/api';
import { Button } from 'primeng/button';
import { Card } from 'primeng/card';
import { Checkbox } from 'primeng/checkbox';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { SplitButton } from 'primeng/splitbutton';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { SERVICE_ENDPOINTS } from '../../../../core/configs/service-endpoints';
import { AuthService } from '../../../../core/services/auth.service';
import { HelperService } from '../../../../core/services/helper.service';
import { I18nService } from '../../../../core/services/i18n.service';
import { UserSessionSettingsProvider } from '../../../../core/services/user-session-settings-provider.service';
import { CustomerPaymentTypeDetailsDialogComponent } from '../../../../custom/components/admin/customer-payment-type-details-dialog/customer-payment-type-details-dialog.component';
import { SpsTableComponent } from '../../../../custom/components/sps-table/sps-table.component';
import { TableConfig } from '../../../../custom/interfaces/sps-table/table-config';
import { TableListProvider } from '../../../../custom/provider/table-list-provider';
import { TableConfigService } from '../../../../custom/services/table-config.service';
import { TableControlService } from '../../../../custom/services/table-control.service';
import { LayoutService } from '../../../../layout/services/layout.service';
import { SidebarMenuService } from '../../../../layout/services/sidebar-menu.service';
import { ContractAppleGoogleCreateDto } from '../../../dtos/config-api/contract-apple-google-create-dto';
import { ContractCreditcardCreateDto } from '../../../dtos/config-api/contract-creditcard-create-dto';
import { ContractDirectTransferCreateDto } from '../../../dtos/config-api/contract-direct-transfer-create-dto';
import { ContractPaypalCreateDto } from '../../../dtos/config-api/contract-paypal-create-dto';
import { ContractSepaCreateDto } from '../../../dtos/config-api/contract-sepa-create.dto';
import { PaymentDataCreate } from '../../../dtos/payment-type/payment-data-create';
import { PaymentDataUpdate } from '../../../dtos/payment-type/payment-data-update';
import { CustomerGroup } from '../../../enums/organisation/customer-group';
import { ShopOnboardingStatus } from '../../../enums/organisation/shop-onboarding-status';
import { ShopType } from '../../../enums/organisation/shop-type';
import { ContractBillingType } from '../../../enums/payment/contract-billing-type';
import { EditPaymentDialogType } from '../../../enums/payment/edit-payment-dialog-type';
import { PaymentMethodName } from '../../../enums/payment/payment-method-name';
import { PspContractStatus } from '../../../enums/payment/psp-contract-status';
import { ActivationServiceResult } from '../../../interfaces/activation/activation-service-result';
import { OrganisationShopConfigData } from '../../../interfaces/organisation/organisation-shop-config-data';
import { EditPaymentDialog } from '../../../interfaces/payment/edit-payment-dialog';
import { PaymentData } from '../../../interfaces/payment/payment-data';
import { PaymentInvalidFlags } from '../../../interfaces/payment/payment-invalid-flags';
import { PaymentTypeContractData } from '../../../interfaces/payment/payment-type-contract-data';
import { ContractDataToContractDtoMapperService } from '../../../mapper/activation/contract-data-to-contract-dto-mapper.service';
import { PaymentTypeToPaymentTypeUpdateDtoMapperService } from '../../../mapper/payment/payment-type-to-payment-type-update-dto-mapper.service';
import { PaymentConfigService } from '../../../services/payment-config.service';
import { PaymentDataService } from '../../../services/payment-data.service';
import { BaseComponent } from '../../base/base.component';
import { AdminConfigResultComponent } from './admin-config-result/admin-config-result.component';
import { AdminPaymentTypeActivationComponent } from './admin-payment-type-activation/admin-payment-type-activation.component';
import { AdminPaymentTypeConfigComponent } from './admin-payment-type-config/admin-payment-type-config.component';
import { AdminPaymentTypeContractComponent } from './admin-payment-type-contract/admin-payment-type-contract.component';
import { AdminPaymentTypeEmailComponent } from './admin-payment-type-email/admin-payment-type-email.component';
import { AdminPaymentTypePayoutComponent } from './admin-payment-type-payout/admin-payment-type-payout.component';

@Component({
  selector: 'app-admin-payment-types-view',
  imports: [
    Button,
    Card,
    PrimeTemplate,
    SplitButton,
    SpsTableComponent,
    TableModule,
    Tooltip,
    Dialog,
    InputText,
    ReactiveFormsModule,
    FormsModule,
    AdminPaymentTypeConfigComponent,
    AdminPaymentTypePayoutComponent,
    AdminPaymentTypeEmailComponent,
    NgClass,
    Select,
    CustomerPaymentTypeDetailsDialogComponent,
    AdminPaymentTypeActivationComponent,
    AdminPaymentTypeContractComponent,
    AdminConfigResultComponent,
    Checkbox
  ],
  templateUrl: './admin-payment-types-view.component.html'
})
export class AdminPaymentTypesViewComponent extends BaseComponent implements OnInit, AfterViewInit {

  /**
   * caller component (customer or onboarding)
   * tenantId from the organisation
   * @private
   */
  private parent: string;
  public tenantId: string;

  /**
   * table-config for organisations-view -> SPS_ORGANISATIONS_VIEW.json
   */
  readonly tableConfig: WritableSignal<TableConfig> = signal(null);
  totalRecords: number = 0;
  rows: number = 20;
  scrollHeightOffset: number = 176;
  minScrollHeight: number = 190;
  tableListProvider: TableListProvider;
  selectedRowData: PaymentData = null;

  /**
   * Params for Data-Refresh-Polling
   * TODO if changed to Lazyloading
   */
  lastLazyLoadingEvent: LazyLoadEvent = null;

  /**
   * Admin split-menu Button
   * @private
   */
  adminMenuActive: boolean = false;
  adminMenuItems: MenuItem[] = [];

  /**
   * model  for table
   */
  paymentTypes: PaymentData[] = [];

  /**
   * Dialog flags for the specific payment edit forms
   * see payment-method-edit.component.ts for the customer portal
   */
  editPaymentData: PaymentData;
  editPaymentDialog: EditPaymentDialog = {visible: false};
  protected readonly PaymentDialogType = EditPaymentDialogType;
  paymentInvalidFlags: PaymentInvalidFlags;
  ibanIsValid: boolean = true;
  shopCreationEnabled: boolean = false;
  activationEnabled: boolean = false;   // Aktivierung kann durchgeführt werden, Orgadaten sind vollständig
  activationServiceResult: ActivationServiceResult = {status: '', successful: false};
  /**
   * Payment type details dialog
   */
  showPaymentTypeDetailsDialog: boolean = false;

  /**
   * Add payment type dialog params
   */
  showAddPaymentTypeDialog: boolean = false;
  addPaymentInProgress: boolean = false;
  newPaymentType: PaymentData;
  paymentMethodList: SelectItem[] = [];
  protected readonly PaymentMethodName = PaymentMethodName;

  /**
   * Data for the activation process (shop & contract)
   */
  shopConfigData: OrganisationShopConfigData = {};
  paymentTypeContractData: PaymentTypeContractData;
  chargebackCreditCards: SelectItem[]; // chargeback cards for sid chargeback reference project Id

  constructor(private route: ActivatedRoute,
              private router: Router,
              private authService: AuthService,
              public i18n: I18nService,
              private messageService: MessageService,
              private confirmationService: ConfirmationService,
              private helper: HelperService,
              public layoutService: LayoutService,
              private sidebarMenuService: SidebarMenuService,
              private userSessionSettingsProvider: UserSessionSettingsProvider,
              private tableConfigService: TableConfigService,
              public tableControlService: TableControlService,
              protected paymentDataService: PaymentDataService,
              protected paymentConfigService: PaymentConfigService,
              protected paymentTypeUpdateMapper: PaymentTypeToPaymentTypeUpdateDtoMapperService,
              private contractDataDtoMapper: ContractDataToContractDtoMapperService ) {
    super();
  }

  async ngOnInit() {
    this.loading = true;
    this.tableListProvider = new TableListProvider(this.i18n);
    this.tableConfig.set(await this.tableConfigService.getTableConfig('SPS_ADMIN_PAYMENTTYPES_VIEW', false));
    this.route.paramMap.subscribe(params => {
      if (params && (params.get('parent') !== null) && (params.get('tenantId') !== null)) {
        this.parent = params.get('parent');
        this.tenantId = params.get('tenantId');
        this.rows = this.tableConfig().rows;
        if (this.userSessionSettingsProvider.hasValue('ADMIN_PAYMENTTYPES_VIEW_ROWS')) {
          this.rows = this.userSessionSettingsProvider.get('ADMIN_PAYMENTTYPES_VIEW_ROWS');
        }
        this.initAdminMenuItems();
        this.getPaymentTypes();
        this.initPaymentMethodList();
        this.initChargebackCards();
      } else {
        this.router.navigate(['/error/auth']);
      }
    });
  }

  ngAfterViewInit(): void {
    /**
     * Some controls need refresh after language-changes
     */
    this.subscriptions.add(
      this.i18n.languageChanged.subscribe({
        next: value => {
          this.refreshControls = true;
          setTimeout(() => {
            this.refreshControls = false;
          }, 10);
        }
      })
    );

    /**
     * Initialize Breadcrumb-Items
     */
    setTimeout(() => {
      this.i18n.breadcrumbMenuItems.set([
        {
          icon: 'pi pi-cog',
          i18nLabelKey: this.parent === 'customer' ? 'MENUCUSTOMERS' : 'MENUONBOARDING',
          label: this.i18n.i18nLng()['MENUCUSTOMERS'],
          routerLink: this.parent === 'customer' ? ['/admincustomers'] : ['/adminonboarding'],
          routerLinkActive: false
        },
        {
          i18nLabelKey: 'PAYMENTTYPES',
          label: this.i18n.i18nLng()['PAYMENTTYPES'],
          routerLinkActive: true
        }
      ]);
    }, 50);

  }

  /**
   * currently an eager table
   * TODO change to LayzLoading if necessary
   * @private
   */
  private getPaymentTypes(reselect: boolean = false) {
    this.paymentDataService.findPageAdmin(this.helper.eagerLazyLoadEvent(), this.tenantId).subscribe({
      next: result => {
        if (result.successful) {
          /**
           * TODO should be set in api in a future version, on new payment-type
           * TODO implementation for all payment methods
           * provider and acquirer should be set in backend on initialization of a new payment-type
           */
          result.payload.map(pt => {
            switch (pt.paymentMethod) {
              case PaymentMethodName.CREDITCARD:
              case PaymentMethodName.CREDITCARD_MOTO:
                pt.acquirer = 'PAYONE GmbH';
                pt.provider = 'Pagateq (VÖB)';
                break;
              case PaymentMethodName.WERO:
                pt.acquirer = 'PAYONE GmbH';
                pt.provider = 'PAYONE GoPay';
                break;
              case PaymentMethodName.SEPA:
              case PaymentMethodName.SEPA_BLACKLIST:
                pt.acquirer = 'S-Public Services GmbH';
                pt.provider = 'Pagateq (VÖB)';
                break;
              case PaymentMethodName.PAYPAL:
                pt.acquirer = 'PayPal';
                pt.provider = 'Pagateq (VÖB)';
                break;
              case PaymentMethodName.DIRECTTRANSFER:
                pt.acquirer = 'S-Public Services GmbH';
                pt.provider = 'Pagateq (VÖB)';
                break;
              case PaymentMethodName.APPLEPAY:
                pt.acquirer = 'PAYONE GmbH';
                pt.provider = 'Pagateq (VÖB)';
                break;
              case PaymentMethodName.GOOGLEPAY:
                pt.acquirer = 'PAYONE GmbH';
                pt.provider = 'Pagateq (VÖB)';
                break;
            }
          });
          this.paymentTypes = result.payload;
          if (reselect) {
            this.selectedRowData = null;
            setTimeout(() => {
              this.onSelectionChanged(this.editPaymentData);
            }, 10);
          }
        } else {
          this.paymentTypes = [];
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
        this.totalRecords = this.paymentTypes.length;
        this.loading = false;
      },
      error: err => {
        this.paymentTypes = [];
        this.totalRecords = this.paymentTypes.length;
        this.loading = false;
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
      }

    });
  }

  /**
   * Inititalize the menu-items for the admin views
   * of the customer data
   * @private
   */
  private initAdminMenuItems(): void {
    this.adminMenuItems = [];
    /**
     * No contract activation for SID or Uni NRW customers
     */
    if (this.paymentDataService.selectedOrganisationData.customerGroup !== CustomerGroup.SID && this.paymentDataService.selectedOrganisationData.customerGroup !== CustomerGroup.UNINRW) {
      this.adminMenuItems.push(
        {
          id: 'm-activation',
          label: this.i18n.i18nLng()['ACTIVATION'],
          icon: 'pi pi-verified',
          command: () => {
            this.showActivationDialog();
          },
          disabled: true,
        },
      )
    }
    this.adminMenuItems.push(
      {
        id: 'm-configuration',
        label: this.i18n.i18nLng()['MENUCONFIGURATION'],
        icon: 'pi pi-cog',
        command: () => {
          this.showPaymentEditDialog(EditPaymentDialogType.PAYMENTTYPE);
        }
      },
      {
        id: 'm-bank-account',
        label: this.i18n.i18nLng()['PAYOUTDETAILS'],
        icon: 'pi pi-building-columns',
        command: () => {
          this.showPaymentEditDialog(EditPaymentDialogType.PAYOUTDETAILS);
        }
      },
      {
        id: 'm-email-notification',
        label: this.i18n.i18nLng()['EMAILNOTIFICATION'],
        icon: 'pi pi-inbox',
        command: () => {
          this.showPaymentEditDialog(EditPaymentDialogType.EMAILNOTIFICATION);
        }
      },
      {
        separator: true
      },
      {
        id: 'm-add-payment-type',
        label: this.i18n.i18nLng()['ADDPAYMENTTYPE'],
        icon: 'pi pi-plus-circle',
        command: () => {
          this.addPaymentType();
        }
      }
    );
  }

  private initPaymentMethodList() {
    /**
     * check for enabled global payment methods
     * and add to available methods list
     */
    this.paymentDataService.paymentMethodConfig.map(pc => {
      if (pc.enabled && !pc.lockedByAdmin) {
        this.paymentMethodList.push({
          value: pc.name,
          label: this.i18n.i18nLng()[pc.name]
        });
      }
    });
  }

  /**
   * filter cards
   * @private
   */
  private initChargebackCards() {
    this.chargebackCreditCards = [];
    const routeEndpoint = SERVICE_ENDPOINTS.GET_PAYMENT_TYPES_TENANT_CHARGEBACK + this.paymentDataService.selectedOrganisationId + '/chargeback-creditcards';
    this.paymentDataService.findAll(routeEndpoint).subscribe({
      next: (result) => {
        if (result.successful) {
          (result.payload as PaymentData[]).map(paymentType => {
            this.chargebackCreditCards.push({
              value: paymentType.projectId,
              label: paymentType.title + ' (' + paymentType.projectId + ')',
            })
          });
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: result.message,
            detail: result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      },
      error: err => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
        });
      }
    })

  }

  protected clearTableFilters() {
    this.tableControlService.clearTableFilters.set(true);
    this.getPaymentTypes();
  }

  /**
   * if row was selected - open details dialog for specific payment-type
   * @param paymentData
   */
  protected onSelectionChanged(paymentData: PaymentData) {
    this.selectedRowData = null;
    this.adminMenuActive = false;
    if (paymentData) {
      this.selectedRowData = paymentData;
      this.editPaymentData = this.helper.jsonCopy(this.selectedRowData);
      this.adminMenuActive = true;
      /**
       * If a shop creation workflow for the organization or a
       * payment contract creation workflow is pending,
       * the activation menu is disabled
       */
      let activationMenuEnabled: boolean = true;
      /**
       * 1. check if payment type could be activated
       */
      if (this.selectedRowData?.pspContractStatus) {
        if (this.selectedRowData?.pspContractStatus !== PspContractStatus.NOT_CONFIGURED && this.selectedRowData?.pspContractStatus !== PspContractStatus.FAILED) {
          activationMenuEnabled = false;   // shop creation is pending
        }
      }
      /**
       * 2. if payment type could be activated, check if shop should be created
       */
      this.shopCreationEnabled = false;
      if (this.paymentDataService.selectedOrganisationData?.shopOnboardingStatus) {
        if (this.paymentDataService.selectedOrganisationData?.shopOnboardingStatus === ShopOnboardingStatus.NO_SHOP || this.paymentDataService.selectedOrganisationData?.shopOnboardingStatus === ShopOnboardingStatus.FAILED) {
          this.shopCreationEnabled = true;   // shop creation is pending
        }
      }
      /**
       * Diasble activation for wero moto and klarna
       * until it is implemented
       * !! also disabled for DIRECTTRANSFER untils Pagateq-REST-api is implemented
       * TODO enable for DIRECTTRANSFER after api-implementation
       */
      if (this.selectedRowData.paymentMethod === PaymentMethodName.DIRECTTRANSFER || this.selectedRowData.paymentMethod === PaymentMethodName.WERO || this.selectedRowData.paymentMethod === PaymentMethodName.KLARNA) {
        activationMenuEnabled = false;   // shop creation is pending
      }
      try {
        this.adminMenuItems.find(item => item.id === 'm-activation').disabled = !activationMenuEnabled;
      } catch (e) {
      }
    }
  }

  /**
   * Rows changed by user
   * @param newRowCount
   */
  rowsCountChanged(newRowCount: number) {
    this.rows = newRowCount;
    this.userSessionSettingsProvider.set('ADMIN_PAYMENTTYPES_VIEW_ROWS', this.rows);
  }

  /**
   * PaymentType details dialog
   * @protected
   */
  protected showDetailsDialog() {
    this.showPaymentTypeDetailsDialog = true;
  }

  protected closeDetailsDialog() {
    this.showPaymentTypeDetailsDialog = false;
  }

  /**
   * Choose the workflow type (shop + contract or only contract)
   * for the activation workflow and show the dialog
   * @private
   */
  private showActivationDialog() {
    if (this.shopCreationEnabled) {
      this.showPaymentEditDialog(EditPaymentDialogType.ACTIVATION);
    } else {
      this.showPaymentEditDialog(EditPaymentDialogType.PAYMENTCONTRACT);
    }
  }


  /**
   * show the specific edit-dialog for the selected payment type
   * @param type
   * @private
   */
  private showPaymentEditDialog(type: EditPaymentDialogType) {
    this.editPaymentDialog.type = type;
    this.resetPaymentInvalidFlags();
    switch (type) {
      case EditPaymentDialogType.ACTIVATION:
      case EditPaymentDialogType.ACTIVATIONRESULT:
        this.editPaymentDialog.i18nTitleKey = 'ACTIVATION';
        break;
      case EditPaymentDialogType.PAYMENTCONTRACT:
        this.initPaymentTypeConfigData();
        this.editPaymentDialog.i18nTitleKey = 'PAYMENTCONTRACT';
        break;
      case EditPaymentDialogType.PAYMENTTYPE:
        this.editPaymentDialog.i18nTitleKey = 'MENUCONFIGURATION';
        break;
      case EditPaymentDialogType.PAYOUTDETAILS:
        this.editPaymentDialog.i18nTitleKey = 'PAYOUTDETAILS';
        break;
      case EditPaymentDialogType.EMAILNOTIFICATION:
        this.editPaymentDialog.i18nTitleKey = 'EMAILNOTIFICATION';
        break;
    }
    this.editPaymentDialog.visible = true;
  }

  /**
   * close dialog
   * @protected
   */
  protected cancelEditPaymentData(reset: boolean = true, refresh: boolean = false) {
    if (reset) {
      this.editPaymentData = this.helper.jsonCopy(this.selectedRowData);
    }
    this.editPaymentDialog.visible = false;
    if (refresh) {
      this.getPaymentTypes();
    }
  }

  /**
   * before saving...
   * on switching payment type to active state -> check for valid data
   */
  checkValidPaymentDataOnSave(): void {
    this.resetPaymentInvalidFlags();
    let valid: boolean = true;
    switch (this.editPaymentDialog.type) {
      case EditPaymentDialogType.EMAILNOTIFICATION:
        if (this.editPaymentData.emailNotification && (!this.editPaymentData?.emailAddress || this.editPaymentData.emailAddress?.trim().length === 0)) {
          valid = false;
          this.paymentInvalidFlags.emailAddress = true;
        }
        if (this.editPaymentData?.emailAddress) {
          this.editPaymentData.emailAddress = this.editPaymentData.emailAddress.trim();
          if (this.editPaymentData?.emailAddress.length > 0) {
            if (!this.helper.isValidEmailList(this.editPaymentData.emailAddress)) {
              valid = false;
              this.paymentInvalidFlags.invalidEmailFormat = true;
            }
          }
        }
        if (valid) {
          this.savePaymentData();
        }
        break;
      case EditPaymentDialogType.PAYOUTDETAILS:
        /**
         * different Dialog fpr PayPal
         * no Bank-Data for PayPal only the PayPal E-Mail-Address
         */
        switch (this.editPaymentData.paymentMethod) {
          case PaymentMethodName.PAYPAL:
            if (this.editPaymentData?.payPalEmailAddress && this.editPaymentData?.payPalEmailAddress.trim().length > 0) {
              if (!this.helper.isValidEmail(this.editPaymentData.payPalEmailAddress)) {
                valid = false;
                this.paymentInvalidFlags.invalidEmailFormat = true;
              }
            } else {
              valid = false;
              this.paymentInvalidFlags.payPalEmailAddress = true;
            }
            if (valid) {
              this.savePaymentData();
            }
            break;
            default:
              if (!this.editPaymentData?.accountHolder || this.editPaymentData?.accountHolder.trim().length === 0) {
                this.paymentInvalidFlags.accountHolder = true;
                valid = false;
              }
              if (!this.editPaymentData?.IBAN || this.editPaymentData?.IBAN.trim().length === 0 || !IBAN.isValid(this.editPaymentData?.IBAN)) {
                this.paymentInvalidFlags.IBAN = true;
                valid = false;
              }
              if (!this.editPaymentData?.BIC || this.editPaymentData?.BIC.trim().length === 0) {
                this.paymentInvalidFlags.BIC = true;
                valid = false;
              }
              if (!this.editPaymentData?.bank || this.editPaymentData?.bank.trim().length === 0) {
                this.paymentInvalidFlags.bank = true;
                valid = false;
              }
        }
        if (valid) {
          this.savePaymentData();
        }
        break;
      case EditPaymentDialogType.PAYMENTTYPE:
        if (this.editPaymentData.active) {
          /**
           * TODO implement this for all new payment methods
           */
          switch (this.editPaymentData.paymentMethod) {
            case PaymentMethodName.CREDITCARD:
            case PaymentMethodName.CREDITCARD_MOTO:
            case PaymentMethodName.APPLEPAY:
            case PaymentMethodName.GOOGLEPAY:
              if (!this.editPaymentData?.pspMerchantId || this.editPaymentData?.pspMerchantId.trim().length === 0) {
                this.paymentInvalidFlags.pspMerchantId = true;
                valid = false;
              }
              if (!this.editPaymentData?.vuNumber || this.editPaymentData?.vuNumber.trim().length === 0) {
                this.paymentInvalidFlags.vuNumber = true;
                valid = false;
              }
              /**
               * Google Pay merchant id
               */
              if (this.editPaymentData?.paymentMethod === PaymentMethodName.GOOGLEPAY) {
                if (!this.editPaymentData?.googlePayMerchantId || this.editPaymentData?.googlePayMerchantId.trim().length === 0) {
                  this.paymentInvalidFlags.googlePayMerchantId = true;
                  valid = false;
                }
              }
              break;
            case PaymentMethodName.WERO:
              if (!this.editPaymentData?.pspMerchantId || this.editPaymentData?.pspMerchantId.trim().length === 0) {
                this.paymentInvalidFlags.pspMerchantId = true;
                valid = false;
              }
              if (!this.editPaymentData?.apiClientKey || this.editPaymentData?.apiClientKey.trim().length === 0) {
                this.paymentInvalidFlags.apiClientKey = true;
                valid = false;
              }
              if (!this.editPaymentData?.apiClientSecret || this.editPaymentData?.apiClientSecret.trim().length === 0) {
                this.paymentInvalidFlags.apiClientSecret = true;
                valid = false;
              }
              /** TODO if Webhook are enabled and no more polling -> reactivate
              if (!this.editPaymentData?.webhookKey || this.editPaymentData?.webhookKey.trim().length === 0) {
                this.paymentInvalidFlags.webhookKey = true;
                valid = false;
              }
              if (!this.editPaymentData?.webhookSecret || this.editPaymentData?.webhookSecret.trim().length === 0) {
                this.paymentInvalidFlags.webhookSecret = true;
                valid = false;
              }
               */
              try {
                this.editPaymentData.payOnePartnerId = this.editPaymentData?.payOnePartnerId.trim();
              } catch (e) {
              }
              if (this.editPaymentData?.payOnePartnerId &&  this.editPaymentData?.payOnePartnerId.length > 0 && this.editPaymentData?.payOnePartnerId.length < 7) {
                this.paymentInvalidFlags.payOnePartnerId = true;
                valid = false;
              }
              break;
            case PaymentMethodName.SEPA:
            case PaymentMethodName.SEPA_BLACKLIST:
              if (!this.editPaymentData?.pspMerchantId || this.editPaymentData?.pspMerchantId.trim().length === 0) {
                this.paymentInvalidFlags.pspMerchantId = true;
                valid = false;
              }
              break;
            case PaymentMethodName.PAYPAL:
              if (!this.editPaymentData?.pspMerchantId || this.editPaymentData?.pspMerchantId.trim().length === 0) {
                this.paymentInvalidFlags.pspMerchantId = true;
                valid = false;
              }
              if (!this.editPaymentData?.payPalEmailAddress || this.editPaymentData?.payPalEmailAddress.trim().length === 0) {
                this.paymentInvalidFlags.payPalEmailAddress = true;
                valid = false;
              } else {
                if (!this.helper.isValidEmail(this.editPaymentData.payPalEmailAddress)) {
                  this.paymentInvalidFlags.invalidEmailFormat = true;
                  valid = false;
                }
              }
              break;
          }
          /**
           * Check for valid bank-account data
           * when payment type is active
           * only for payment-types with bank-account data
           */
          if (valid) {
            switch (this.editPaymentData.paymentMethod) {
              case PaymentMethodName.CREDITCARD:
              case PaymentMethodName.CREDITCARD_MOTO:
              case PaymentMethodName.SEPA:
              case PaymentMethodName.SEPA_BLACKLIST:
              case PaymentMethodName.WERO:
              case PaymentMethodName.DIRECTTRANSFER:
              case PaymentMethodName.APPLEPAY:
              case PaymentMethodName.GOOGLEPAY:
                if (!this.editPaymentData?.accountHolder || this.editPaymentData?.accountHolder.trim().length === 0) {
                  valid = false;
                }
                if (!this.editPaymentData?.IBAN || this.editPaymentData?.IBAN.trim().length === 0 || !IBAN.isValid(this.editPaymentData?.IBAN)) {
                  valid = false;
                }
                if (!this.editPaymentData?.BIC || this.editPaymentData?.BIC.trim().length === 0) {
                  valid = false;
                }
                if (!this.editPaymentData?.bank || this.editPaymentData?.bank.trim().length === 0) {
                  valid = false;
                }
                if (!valid) {
                  this.confirmationService.confirm({
                    position: 'center',
                    message: this.i18n.i18nLng()['PAYMENTACTIVATIONWARNING'],
                    header: this.i18n.i18nLng()['CONFIRMATION'],
                    closable: true,
                    closeOnEscape: true,
                    icon: 'pi pi-question-circle',
                    acceptButtonProps: {
                      label: 'OK'
                    },
                    rejectButtonProps: {
                      label: this.i18n.i18nLng()['CANCEL'],
                      outlined: true,
                      severity: 'secondary'
                    },
                    accept: () => {
                      this.editPaymentData.active = false;
                      this.savePaymentData();
                    }
                  });
                } else {
                  this.savePaymentData();
                }
                break;
              default:
                this.savePaymentData();
            }
          }
          break;
        } else {
          /**
           * payment type is not active, check only for valid formats, like E-Mail etc.
           */
          switch (this.editPaymentData.paymentMethod) {
            case PaymentMethodName.PAYPAL:
              /**
               * check for a valid PAYPAL E-Mail Address, only when an address was entered
               */
              if (this.editPaymentData?.payPalEmailAddress && this.editPaymentData?.payPalEmailAddress.trim().length > 0) {
                if (!this.helper.isValidEmail(this.editPaymentData.payPalEmailAddress)) {
                  valid = false;
                  this.paymentInvalidFlags.invalidEmailFormat = true;
                }
              }
              break;
            case PaymentMethodName.WERO:
              /**
               * check for a valid PAYONE Partner ID, only if an ID was entered
               */
              try {
                this.editPaymentData.payOnePartnerId = this.editPaymentData?.payOnePartnerId.trim();
              } catch (e) {
              }
              if (this.editPaymentData?.payOnePartnerId &&  this.editPaymentData?.payOnePartnerId.length > 0 && this.editPaymentData?.payOnePartnerId.length < 7) {
                this.paymentInvalidFlags.payOnePartnerId = true;
                valid = false;
              }
              break;
          }
          if (valid) {
            this.savePaymentData();
          }
        }
    }
  }

  /**
   * save payment type data
   * @protected
   */
  private savePaymentData() {
    const paymentDataDto: PaymentDataUpdate = this.paymentTypeUpdateMapper.map(this.editPaymentData);
    this.paymentDataService.update(SERVICE_ENDPOINTS.UPDATE_PAYMENT_TYPE + paymentDataDto.id, paymentDataDto).subscribe({
      next: result => {
        if (result.successful) {
          this.messageService.add({
            life: 3500,
            severity: 'success',
            summary: this.i18n.i18nLng()['PAYMENTMETHOD'] + ' ' + this.i18n.i18nLng()[paymentDataDto.paymentMethod],
            detail: this.i18n.i18nLng()['HTTPDATAUPDATESUCCESS']
          });
          this.editPaymentDialog.visible = false;
          this.getPaymentTypes(true);
          this.paymentDataService.initTenantPaymentData();
          this.initChargebackCards();
        } else {
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      },
      error: error => {
        this.messageService.add({
          sticky: true,
          severity: 'error',
          summary: this.i18n.i18nLng()['HTTPDATAERROR'],
          detail: error.message
        });
      }
    });

  }

  /**
   * Initialize the default data for the shop and the contract activation dialog
   * @private
   */
  private initPaymentTypeConfigData(): void {
    this.shopConfigData = {
      // tenantId: this.editPaymentData?.tenantId,
      tenantId: this.paymentDataService.selectedOrganisationData.displayCustomerNumber,
      shopType: ShopType.RETAIL,
    }
    /**
     * TODO - Implementation for all payment types
     */
    switch (this.editPaymentData.paymentMethod) {
      case PaymentMethodName.CREDITCARD:
      case PaymentMethodName.CREDITCARD_MOTO:
        this.paymentTypeContractData = {
          paymentMethod: this.selectedRowData.paymentMethod,
          vuNumber: this.selectedRowData.vuNumber,
          currency: 'EUR',
        }
        break;
      case PaymentMethodName.PAYPAL:
        this.paymentTypeContractData = {
          paymentMethod: this.selectedRowData.paymentMethod,
          payPalEmailAddress: this.selectedRowData.payPalEmailAddress,
          payPalMerchantId: this.selectedRowData.payPalMerchantId,
        }
        break;
      case PaymentMethodName.SEPA:
      case PaymentMethodName.SEPA_BLACKLIST:
        this.paymentTypeContractData = {
          paymentMethod: this.selectedRowData.paymentMethod,
          creditorId: this.paymentDataService.selectedOrganisationData.creditorId,
          accountHolder: this.selectedRowData.accountHolder,
          IBAN: this.selectedRowData.IBAN,
          BIC: this.selectedRowData.BIC,
          billingType: ContractBillingType.BATCH,
        }
        break;
      case PaymentMethodName.DIRECTTRANSFER:
      case PaymentMethodName.APPLEPAY:
      case PaymentMethodName.GOOGLEPAY:
        this.paymentTypeContractData = {
          paymentMethod: this.selectedRowData.paymentMethod,
        };
        break;
    }
  }

  /**
   * Checked the validity of the IBAN
   * @param valid
   */
  onIbanValidated(valid: boolean) {
    this.ibanIsValid = valid;
    this.paymentInvalidFlags.IBAN = !valid;
  }

  /**
   * Reset all invalid flags for payment data
   * @private
   */
  private resetPaymentInvalidFlags() {
    this.paymentInvalidFlags = {
      paymentMethod: false,
      title: false,
      accountHolder: false,
      IBAN: false,
      BIC: false,
      bank: false,
      emailAddress: false,
      invalidEmailFormat: false,
      pspMerchantId: false,
      vuNumber: false,
      apiClientKey: false,
      apiClientSecret: false,
      payOnePartnerId: false,
      payPalEmailAddress: false,
      googlePayMerchantId: false,
    };
  }

  protected addPaymentType() {
    /**
     * Init data for new payment type
     */
    this.newPaymentType = {
      id: null,
      status: 'TEST',
      paymentMethod: null,
      title: null
    };
    this.resetPaymentInvalidFlags();
    this.showAddPaymentTypeDialog = true;
  }

  protected cancelAddPaymentType() {
    this.showAddPaymentTypeDialog = false;
  }

  /**
   * Save new (added) payment type for the current organization
   * @protected
   */
  protected saveNewPaymentType() {
    if (!this.addPaymentInProgress) {
      this.addPaymentInProgress = true;

      /**
       * allways reset chargeback if payment method is not creditcard
       */
      if (this.newPaymentType.paymentMethod !== PaymentMethodName.CREDITCARD) {
        this.newPaymentType.isChargebackCard = false;
      }
      /**
       * Check valid payment data
       */
      let valid: boolean = true;
      this.resetPaymentInvalidFlags();

      /**
       * in TEST-Mode IBAN is not a required field
       */
      if (!this.newPaymentType?.paymentMethod || this.newPaymentType.paymentMethod?.trim().length === 0) {
        valid = false;
        this.paymentInvalidFlags.paymentMethod = true;
      }
      if (!this.newPaymentType?.title || this.newPaymentType.title?.trim().length === 0) {
        valid = false;
        this.paymentInvalidFlags.title = true;
      }
      if (valid) {

        let paymentDataDto: PaymentDataCreate = {
          paymentMethod: this.newPaymentType.paymentMethod,
          customIcon: false,
          title: this.newPaymentType.title,
          status: this.newPaymentType.status,
          isChargebackCard: this.newPaymentType.isChargebackCard,
        };
        paymentDataDto = this.paymentDataService.setIconAndProvider(paymentDataDto);

        this.paymentDataService.create(SERVICE_ENDPOINTS.CREATE_PAYMENT_TYPE_ADMIN + this.tenantId, paymentDataDto).subscribe({
          next: (result) => {
            if (result.successful) {
              this.showAddPaymentTypeDialog = false;
              this.getPaymentTypes();
              this.initChargebackCards();
              /**
               * if tenantId equal user tenantId then the TenantPaymentData should be refreshed
               */
              if (this.tenantId === this.authService.organisationData.zbvOrganisationId.toString()) {
                const subscription: Subscription = this.paymentDataService.initTenantPaymentData().subscribe(fetched => {
                  subscription.unsubscribe();
                  if (fetched) {
                    this.sidebarMenuService.refreshPaymentMenuItems();
                  }
                });
              }
              this.messageService.add({
                life: 3500,
                severity: 'success',
                summary: this.i18n.i18nLng()['ADDPAYMENTTYPE'],
                detail: this.i18n.i18nLng()['HTTPDATACREATESUCCESS']
              });
            } else {
              this.messageService.add({
                sticky: true,
                severity: 'error',
                summary: this.i18n.i18nLng()['HTTPDATAERROR'],
                detail: result.message + '\n' + result.details + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
              });
            }
            this.addPaymentInProgress = false;
          },
          error: err => {
            this.addPaymentInProgress = false;
            this.messageService.add({
              sticky: true,
              severity: 'error',
              summary: this.i18n.i18nLng()['HTTPDATAERROR'],
              detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
            });
          }
        });
      } else {
        this.addPaymentInProgress = false;
      }
    }
  }

  /**
   * Next button clicked in one of the onboarding activation dialogs
   * @protected
   */
  protected processShopOnboarding() {
    if (this.editPaymentDialog.type === EditPaymentDialogType.ACTIVATION) {
      this.showPaymentEditDialog(EditPaymentDialogType.PAYMENTCONTRACT);
    } else {

      this.layoutService.progressDialogTitle = this.i18n.i18nLng()['ACTIVATIONPROGRESS'];
      this.layoutService.showProgressSpinner = true;
      this.layoutService.showProgressDialog = true;

      /**
       * dto definition for all payment types
       */
      let contractDto: ContractCreditcardCreateDto | ContractPaypalCreateDto | ContractSepaCreateDto | ContractDirectTransferCreateDto | ContractAppleGoogleCreateDto;   // extend other dto's with |

      /**
       * TODO Implementation for all payment types
       */
      switch (this.editPaymentData.paymentMethod) {
        case PaymentMethodName.CREDITCARD:
          contractDto = this.contractDataDtoMapper.map(this.shopConfigData, this.paymentTypeContractData, PaymentMethodName.CREDITCARD, this.editPaymentData.id) as ContractCreditcardCreateDto;
          break;
        case PaymentMethodName.CREDITCARD_MOTO:
          contractDto = this.contractDataDtoMapper.map(this.shopConfigData, this.paymentTypeContractData, PaymentMethodName.CREDITCARD_MOTO, this.editPaymentData.id) as ContractCreditcardCreateDto;
          break;
        case PaymentMethodName.PAYPAL:
          contractDto = this.contractDataDtoMapper.map(this.shopConfigData, this.paymentTypeContractData, PaymentMethodName.PAYPAL, this.editPaymentData.id) as ContractPaypalCreateDto;
          break;
        case PaymentMethodName.SEPA:
        case PaymentMethodName.SEPA_BLACKLIST:
          contractDto = this.contractDataDtoMapper.map(this.shopConfigData, this.paymentTypeContractData, this.editPaymentData.paymentMethod, this.editPaymentData.id) as ContractSepaCreateDto;
          break;
        case PaymentMethodName.DIRECTTRANSFER:
          contractDto = this.contractDataDtoMapper.map(this.shopConfigData, this.paymentTypeContractData, PaymentMethodName.DIRECTTRANSFER, this.editPaymentData.id) as ContractDirectTransferCreateDto;
          break;
        case PaymentMethodName.APPLEPAY:
        case PaymentMethodName.GOOGLEPAY:
          contractDto = this.contractDataDtoMapper.map(this.shopConfigData, this.paymentTypeContractData, this.editPaymentData.paymentMethod, this.editPaymentData.id) as ContractAppleGoogleCreateDto;
          break;
      }

      this.paymentConfigService.createConfig(SERVICE_ENDPOINTS.CREATE_SHOP_CONTRACT, contractDto).subscribe({
        next: (result) => {
          /**
           * update shop status on buffering service
           */
          this.activationServiceResult = result;
          this.layoutService.showProgressDialog = false;
          if (result.successful) {
            /**
             * refresh all data
             */
            this.getPaymentTypes();
            this.initChargebackCards();
            this.paymentDataService.selectedOrganisationData.shopOnboardingStatus = result.shopOnboardingStatus;
          }
          /**
           * show config api result dialog
           */
          this.showPaymentEditDialog(EditPaymentDialogType.ACTIVATIONRESULT);
        },
        error: err => {
          this.layoutService.showProgressDialog = false;
          this.messageService.add({
            sticky: true,
            severity: 'error',
            summary: this.i18n.i18nLng()['HTTPDATAERROR'],
            detail: err.message + '\n' + this.i18n.i18nLng()['CONTACTSUPPORT']
          });
        }
      });
    }
  }

  /**
   * event for LazyLoading Tables after filtering
   * @param totalRecords
   * @protected
   */
  protected onTotalRecordsChanged(totalRecords: number) {
    this.totalRecords = totalRecords;
  }

  /**
   * output from dialog as signal, if organisation data is valid
   * @param enabled
   * @protected
   */
  protected enableActivation(enabled: boolean) {
    this.activationEnabled = enabled;
  }

  /**
   * shop data changed in dialog
   * @param shopData
   * @protected
   */
  protected onShopDataChanged(shopData: OrganisationShopConfigData) {
    this.shopConfigData = this.helper.jsonCopy(shopData);
  }

  protected readonly CustomerGroup = CustomerGroup;
}
